import * as XLSX from "xlsx";

const REQUIRED_FIELDS = ["flight_number", "aircraft_reg", "departure_airport", "arrival_airport", "std", "sta"];

const FIELD_ALIASES = {
    date: ["date", "date adep [plan][utc]", "日期", "航班日期", "flight date", "dep date", "出发日期", "flight_date"],
    flight_number: ["flight", "flight no", "iata flight number", "航班号", "flight number", "flt", "flt no", "flight_number"],
    departure_airport: ["dep", "adep", "adep iata [plan]", "departure", "出发", "起飞机场", "from", "origin", "dep airport", "dep stn", "departure_airport"],
    arrival_airport: ["arr", "ades", "ades iata [plan]", "arrival", "到达", "降落机场", "to", "destination", "arr airport", "arr stn", "arrival_airport"],
    std: ["std", "std [utc]", "scheduled time of departure", "计划起飞时间", "dep time", "etd"],
    sta: ["sta", "sta [utc]", "scheduled time of arrival", "计划到达时间", "arr time", "eta"],
    aircraft_reg: ["a/c reg", "aircraft reg", "注册号", "registration", "ac reg", "ac registration", "aircraft_reg"],
    stc_type: ["stc", "sector type", "航段类型", "flight type", "service type", "stc_type"],
    block_time_minutes: ["bh", "bhg", "block hours", "block time", "飞行小时", "flight time", "block_time_minutes"],
    remarks: ["remarks", "备注", "notes", "comment", "comments"],
};

function normalizeHeader(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\s\-_./\\()（）\[\]【】{}:：]+/g, "");
}

function normalizeCellValue(value) {
    if (value == null) {
        return "";
    }
    return String(value).trim();
}

function rowHasContent(row) {
    return row.some((cell) => normalizeCellValue(cell));
}

function trimTrailingEmptyCells(row) {
    const next = [...row];
    while (next.length && !normalizeCellValue(next[next.length - 1])) {
        next.pop();
    }
    return next;
}

function buildAliasLookup() {
    const lookup = new Map();
    Object.entries(FIELD_ALIASES).forEach(([field, aliases]) => {
        [field, ...aliases].forEach((alias) => lookup.set(normalizeHeader(alias), field));
    });
    return lookup;
}

const ALIAS_LOOKUP = buildAliasLookup();

function scoreHeaderRow(row) {
    const matchedFields = new Set();
    row.forEach((cell) => {
        const field = ALIAS_LOOKUP.get(normalizeHeader(cell));
        if (field) {
            matchedFields.add(field);
        }
    });
    const requiredCount = REQUIRED_FIELDS.filter((field) => matchedFields.has(field)).length;
    return {
        matchedCount: matchedFields.size,
        requiredCount,
        score: requiredCount * 4 + matchedFields.size,
    };
}

export function detectTableHeaderRowIndex(rows, maxScanRows = 8) {
    let fallbackIndex = -1;
    let best = { index: -1, score: -1, matchedCount: 0, requiredCount: 0 };

    rows.slice(0, maxScanRows).forEach((row, index) => {
        if (!rowHasContent(row)) {
            return;
        }
        if (fallbackIndex === -1) {
            fallbackIndex = index;
        }
        const score = scoreHeaderRow(row);
        if (score.score > best.score) {
            best = { index, ...score };
        }
    });

    if (best.index !== -1 && (best.requiredCount >= 2 || best.matchedCount >= 3)) {
        return best.index;
    }
    return fallbackIndex === -1 ? 0 : fallbackIndex;
}

export function buildTableImportModelFromAoA(rows, options = {}) {
    const normalizedRows = (rows || []).map((row) => (Array.isArray(row) ? row.map(normalizeCellValue) : [normalizeCellValue(row)]));
    const headerRowIndex = detectTableHeaderRowIndex(normalizedRows);
    const rawHeaders = trimTrailingEmptyCells(normalizedRows[headerRowIndex] || []);
    const headers = rawHeaders.length ? rawHeaders : ["Column 1"];
    const dataRows = normalizedRows.slice(headerRowIndex + 1)
        .map((row, index) => ({
            values: headers.map((_, columnIndex) => normalizeCellValue(row[columnIndex])),
            row_number: headerRowIndex + index + 2,
        }))
        .filter((row) => rowHasContent(row.values));

    return {
        input_kind: options.inputKind || "PASTED_TABLE",
        source_name: options.sourceName,
        headers,
        rows: dataRows,
    };
}

function buildParseErrorModel(message, options = {}) {
    return {
        input_kind: options.inputKind || "PASTED_TABLE",
        source_name: options.sourceName || "粘贴表格",
        headers: [],
        rows: [],
        parse_error: message,
    };
}

export function buildTableImportModelFromHtml(html, options = {}) {
    const text = String(html || "").trim();
    if (!text || typeof DOMParser === "undefined") {
        return null;
    }
    const document = new DOMParser().parseFromString(text, "text/html");
    const table = document.querySelector("table");
    if (!table) {
        return null;
    }
    const rows = Array.from(table.querySelectorAll("tr"))
        .map((row) => Array.from(row.querySelectorAll("th,td")).map((cell) => normalizeCellValue(cell.textContent)))
        .filter(rowHasContent);
    if (!rows.length) {
        return null;
    }
    return buildTableImportModelFromAoA(rows, {
        inputKind: "PASTED_TABLE",
        sourceName: options.sourceName || "粘贴表格",
    });
}

export function buildTableImportModelFromPastedText(text, options = {}) {
    const normalizedText = String(text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
    if (!normalizedText) {
        return null;
    }
    if (!/[\t\n,]/.test(normalizedText)) {
        return buildParseErrorModel("粘贴内容未包含 Tab、换行或逗号分隔符，无法确定表格列。请直接从 Excel 复制单元格区域后粘贴，或上传 Excel 文件。", options);
    }
    const delimiter = normalizedText.includes("\t") ? "\t" : ",";
    const rows = normalizedText
        .split("\n")
        .map((line) => line.split(delimiter))
        .filter(rowHasContent);
    if (!rows.length) {
        return null;
    }
    return buildTableImportModelFromAoA(rows, {
        inputKind: "PASTED_TABLE",
        sourceName: options.sourceName || "粘贴表格",
    });
}

export async function buildTableImportModelFromExcelFile(file) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array", cellDates: false });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
        return null;
    }
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });
    return buildTableImportModelFromAoA(rows, {
        inputKind: "EXCEL_FILE",
        sourceName: `${file.name || "Excel 文件"} · ${sheetName}`,
    });
}
